/**
 * Thin fetch wrapper for the TransectLibre API: JSON in/out, CSRF header on
 * mutations, typed errors, and a hook for 401 handling (session expired).
 */
import type { ApiError } from '../../shared/api.js';
import { isGraphFeature, permissionText } from './graphFeatures.js';

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Called once on any 401 response (e.g. to redirect to the login page). */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError | null,
    message: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const res = await fetch(path, { method, headers, body: payload, credentials: 'same-origin', signal: opts.signal });
  if (res.status === 401) unauthorizedHandler?.();
  if (!res.ok) {
    let parsed: ApiError | null = null;
    try {
      parsed = (await res.json()) as ApiError;
    } catch {
      parsed = null;
    }
    throw new HttpError(res.status, parsed, parsed?.message ?? `Verzoek mislukt (HTTP ${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return (await res.text()) as T;
  return (await res.json()) as T;
}

/** Mutations always send a JSON body ({} when none is given); an explicit null is sent as JSON null (e.g. to unlink). */
const bodyOrEmpty = (body: unknown) => (body === undefined ? {} : body);

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, bodyOrEmpty(body), opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, bodyOrEmpty(body), opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, bodyOrEmpty(body), opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
};

/**
 * Multipart upload with progress (fetch cannot report upload progress).
 * Same error handling as `api`: HttpError on non-2xx, 401 hook, CSRF header.
 */
export function uploadWithProgress<T>(path: string, form: FormData, onProgress: (fraction: number) => void, opts: RequestOptions = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status === 401) unauthorizedHandler?.();
      let parsed: unknown = null;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        parsed = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }
      const body = parsed as ApiError | null;
      reject(new HttpError(xhr.status, body, body?.message ?? `Uploaden mislukt (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new TypeError('Netwerkfout tijdens het uploaden'));
    xhr.onabort = () => reject(new DOMException('Upload afgebroken', 'AbortError'));
    if (opts.signal) {
      if (opts.signal.aborted) {
        reject(new DOMException('Upload afgebroken', 'AbortError'));
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}

/** Builds a query string, skipping null/undefined/empty values. Returns "" or "?a=1&b=2". */
export function qs(params: Record<string, string | number | boolean | null | undefined | readonly (string | number)[]>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    sp.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Human-readable message for any thrown value (Dutch). A missing Microsoft 365
 * permission always reads the same per feature, so a burst of such failures
 * collapses into one toast.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    const feature = (err.body?.details as { feature?: unknown } | undefined)?.feature;
    if (err.body?.error === 'graph_permission' && isGraphFeature(feature)) return permissionText(feature);
    return err.message;
  }
  if (isNetworkError(err)) return 'Geen verbinding met de server. Controleer je internetverbinding.';
  if (err instanceof Error) return err.message;
  return 'Er ging iets mis.';
}

/** fetch()/XHR failures without a response (offline, DNS, connection reset). */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && /fetch|network|netwerk|load failed/i.test(err.message);
}
